<template>
    <div>
      <Navbar />
  
      <main>
  
        <!-- ── Page Hero ── -->
        <section class="page-hero">
          <div class="page-hero-overlay"></div>
          <div class="page-container">
            <div class="page-hero-content">
              <h1 class="page-hero-title">Frequently Asked Questions</h1>
              <nav class="breadcrumb-nav" aria-label="Breadcrumb">
                <router-link to="/home" class="breadcrumb-link">Home</router-link>
                <span class="breadcrumb-sep">/</span>
                <span class="breadcrumb-current">Frequently Asked Questions</span>
              </nav>
            </div>
          </div>
        </section>
  
        <!-- ── FAQ Section ── -->
        <section class="section-pad section-gray">
          <div class="page-container faq-shell">
            <div class="section-header">
              <span class="section-badge">Support Center</span>
              <h2 class="section-title">Our Basic Helps!</h2>
              <p class="section-body">
                Find quick answers to the most common questions about using Mapiibs, managing your account,
                invoicing, payroll automation, inventory processing and finance workflows.
              </p>
            </div>
  
            <div v-if="loading" class="loading-state">
              <div class="spinner-border text-primary" role="status" style="width:2.5rem;height:2.5rem;"></div>
            </div>
  
            <div v-else-if="details.length" class="faq-accordion">
              <div
                v-for="(faq, index) in details"
                :key="faq.id"
                class="faq-item"
              >
                <button
                  class="faq-question"
                  @click="toggleFaq(index)"
                  :class="{ active: activeIndex === index }"
                >
                  <span>{{ faq.question }}</span>
                  <font-awesome-icon icon="chevron-down" class="faq-arrow" />
                </button>
  
                <transition name="faq-slide">
                  <div
                    v-if="activeIndex === index"
                    class="faq-answer"
                    v-html="faq.answer"
                  ></div>
                </transition>
              </div>
            </div>
          </div>
        </section>
  
        <!-- ── Assistance Section ── -->
        <section class="section-pad">
          <div class="page-container">
            <div class="section-header">
              <h2 class="section-title">Need personal assistance?</h2>
              <p class="section-body">
                If you cannot find the exact answer you need, our team is available through multiple channels
                to assist you quickly and professionally.
              </p>
            </div>
  
            <div class="support-grid">
              <div class="support-card">
                <div class="support-icon-wrap">
                  <font-awesome-icon icon="comments" class="support-icon" />
                </div>
                <h4 class="support-title">Ask on Whatsapp</h4>
                <p class="support-desc">
                  Send your questions directly through WhatsApp and one of our specialists will respond promptly.
                </p>
                <a
                  href="https://wa.me/+447414491065"
                  target="_blank"
                  class="btn-primary-md"
                >
                  Chat Us
                </a>
              </div>
  
              <div class="support-card support-card-featured">
                <div class="support-icon-wrap featured">
                  <font-awesome-icon icon="life-ring" class="support-icon" />
                </div>
                <h4 class="support-title white">We can help!</h4>
                <p class="support-desc support-desc-light">
                  Submit your request directly to our support desk and receive a prompt personalized response.
                </p>
                <router-link to="/contact" class="btn-white-lg">
                  Submit Request
                </router-link>
              </div>
  
              <div class="support-card">
                <div class="support-icon-wrap">
                  <font-awesome-icon icon="book-open" class="support-icon" />
                </div>
                <h4 class="support-title">Visit Our Blog</h4>
                <p class="support-desc">
                  Explore product tutorials, business automation guides, and finance workflow articles.
                </p>
                <router-link to="/blog" class="btn-primary-md">
                  Visit Now
                </router-link>
              </div>
            </div>
          </div>
        </section>
  
        <!-- ── CTA Band ── -->
        <section class="cta-band">
          <div class="page-container cta-band-inner">
            <div>
              <h2 class="cta-title">Still have more questions?</h2>
              <p class="cta-sub">
                Let Mapiibs help you simplify finance, payroll, invoicing and inventory operations in one place.
              </p>
            </div>
            <router-link to="/auth/register" class="btn-white-lg">
              Get Started Now <font-awesome-icon icon="arrow-right" class="ms-2" />
            </router-link>
          </div>
        </section>
  
      </main>
  
      <Footer />
    </div>
  </template>
  
  <script>
  import Navbar from "../components/Navbar";
  import Footer from "../components/footer";
  import axios from "axios";
  
  export default {
    name: "FaqPage",
  
    components: {
      Navbar,
      Footer,
    },
  
    data() {
      return {
        details: [],
        loading: true,
        activeIndex: 0,
      };
    },
  
    mounted() {
      this.scrollTop();
      this.pageData();
    },
  
    methods: {
      scrollTop() {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      },
  
      async pageData() {
        try {
          const response = await axios.get("/admin/active-faq");
          this.details = response.data.data;
        } catch (error) {
          console.log(error);
        } finally {
          this.loading = false;
        }
      },
  
      toggleFaq(index) {
        this.activeIndex = this.activeIndex === index ? null : index;
      },
    },
  };
  </script>
  
  <style scoped>
  /* ── Shared ───────────────────────────────────────── */
  .page-container {
    max-width: 1160px;
    margin: 0 auto;
    padding: 0 24px;
  }
  .section-pad {
    padding: 80px 0;
  }
  .section-gray {
    background: #f4f7fb;
  }
  .ms-2 {
    margin-left: 8px;
  }
  
  .section-header {
    text-align: center;
    max-width: 720px;
    margin: 0 auto 52px;
  }
  .section-badge {
    display: inline-block;
    background: rgba(0, 74, 173, 0.1);
    color: #004aad;
    border: 1px solid rgba(0, 74, 173, 0.2);
    border-radius: 20px;
    padding: 4px 14px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: 14px;
  }
  .section-title {
    font-size: clamp(24px, 3.5vw, 34px);
    font-weight: 700;
    color: #1a2b45;
    margin: 0 0 14px;
    line-height: 1.3;
  }
  .section-body {
    font-size: 15px;
    color: #6b7a99;
    line-height: 1.7;
    margin: 0;
  }
  
  /* ── Hero ─────────────────────────────────────────── */
  .page-hero {
    position: relative;
    background: linear-gradient(135deg, #051b35 0%, #004aad 100%);
    padding: 100px 0 64px;
    overflow: hidden;
  }
  .page-hero::before {
    content: "";
    position: absolute;
    inset: 0;
    background: url("/static/img/bg-1.jpg") center/cover no-repeat;
    opacity: 0.08;
  }
  .page-hero-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(5, 27, 53, 0.92) 0%, rgba(0, 74, 173, 0.75) 100%);
  }
  .page-hero-content {
    position: relative;
    z-index: 1;
    text-align: center;
  }
  .page-hero-title {
    font-size: clamp(32px, 6vw, 52px);
    font-weight: 800;
    color: #fff;
    margin: 0 0 18px;
  }
  
  /* ── Breadcrumb ───────────────────────────────────── */
  .breadcrumb-nav {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
  }
  .breadcrumb-link {
    color: rgba(255, 255, 255, 0.7);
    text-decoration: none;
  }
  .breadcrumb-link:hover {
    color: #fff;
  }
  .breadcrumb-sep {
    color: rgba(255, 255, 255, 0.3);
  }
  .breadcrumb-current {
    color: rgba(255, 255, 255, 0.55);
  }
  
  /* ── FAQ ──────────────────────────────────────────── */
  .loading-state {
    display: flex;
    justify-content: center;
    padding: 48px 0;
  }
  .faq-shell {
    max-width: 860px;
  }
  .faq-accordion {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  .faq-item {
    background: #fff;
    border: 1px solid #e4eaf2;
    border-radius: 14px;
    overflow: hidden;
    transition: box-shadow 0.2s;
  }
  .faq-item:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
  }
  .faq-question {
    width: 100%;
    border: none;
    background: #fff;
    padding: 22px 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    text-align: left;
    font-size: 15px;
    font-weight: 700;
    color: #1a2b45;
    cursor: pointer;
  }
  .faq-question.active {
    color: #004aad;
  }
  .faq-arrow {
    transition: transform 0.25s ease;
  }
  .faq-question.active .faq-arrow {
    transform: rotate(180deg);
  }
  .faq-answer {
    padding: 0 24px 24px;
    font-size: 14px;
    color: #6b7a99;
    line-height: 1.8;
  }
  
  /* ── Support Cards ───────────────────────────────── */
  .support-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
  }
  .support-card {
    background: #fff;
    border: 1px solid #e4eaf2;
    border-radius: 14px;
    padding: 32px 26px;
    transition: 0.2s;
  }
  .support-card:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
  .support-card-featured {
    background: linear-gradient(135deg, #004aad, #0059d6);
    border: none;
  }
  .support-icon-wrap {
    width: 52px;
    height: 52px;
    border-radius: 12px;
    background: rgba(0, 74, 173, 0.08);
    color: #004aad;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 18px;
  }
  .support-icon-wrap.featured {
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
  }
  .support-title {
    font-size: 18px;
    font-weight: 700;
    color: #1a2b45;
    margin: 0 0 10px;
  }
  .support-title.white {
    color: #fff;
  }
  .support-desc {
    font-size: 14px;
    color: #6b7a99;
    line-height: 1.7;
    margin-bottom: 22px;
  }
  .support-desc-light {
    color: rgba(255, 255, 255, 0.78);
  }
  
  /* ── Buttons ─────────────────────────────────────── */
  .btn-primary-md,
  .btn-white-lg {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    text-decoration: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 700;
    padding: 11px 24px;
  }
  .btn-primary-md {
    background: #004aad;
    color: #fff;
  }
  .btn-primary-md:hover {
    background: #003b8e;
    color: #fff;
  }
  .btn-white-lg {
    background: #fff;
    color: #004aad;
  }
  .btn-white-lg:hover {
    background: #e8f0fb;
  }
  
  /* ── CTA Band ────────────────────────────────────── */
  .cta-band {
    background: linear-gradient(135deg, #004aad, #0059d6);
    padding: 56px 0;
  }
  .cta-band-inner {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
    flex-wrap: wrap;
  }
  .cta-title {
    font-size: 26px;
    font-weight: 700;
    color: #fff;
    margin: 0 0 8px;
  }
  .cta-sub {
    font-size: 15px;
    color: rgba(255, 255, 255, 0.75);
    margin: 0;
  }
  
  /* ── Responsive ──────────────────────────────────── */
  @media (max-width: 900px) {
    .support-grid {
      grid-template-columns: 1fr;
    }
  }
  
  @media (max-width: 768px) {
    .section-pad {
      padding: 56px 0;
    }
    .page-hero {
      padding: 80px 0 52px;
    }
  }
  </style>